import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import SalesforceImgSrc from '../../../../assets/images/integrations/salesforce16.svg';
import {buildContactsManagerAccountsUrl} from '../../../../helpers/contactsManagerHelpers';
import {SetupGuideTask, SetupGuideTaskProps} from '../common/setupGuideTask';

/*
 * Constants.
 */

const contactsManagerAccountsUrl = buildContactsManagerAccountsUrl();

/*
 * Props.
 */

type AddAccountTaskProps = Omit<SetupGuideTaskProps, 'label' | 'onClick'> & {
  onCreateClick: Handler;
  onImportCSVClick?: Handler;
  onImportSalesforceClick?: Handler;
  navigateToLink: HandlerOf<string>;
};

/*
 * Styles.
 */

const StyledSimpleDropdown = styled(SimpleDropdown)`
  max-width: 216px;
`;

const StyledDropdownDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

/*
 * Component.
 */

export const AddAccountTask: FC<AddAccountTaskProps> = ({
  isComplete,
  onCreateClick,
  onImportCSVClick,
  onImportSalesforceClick,
  navigateToLink,
}) => {
  // The below handlers are only available in the Contacts Manager. If they are not
  // present we navigate to the Contacts Manager rather than open a popover.
  if (!onImportCSVClick || !onImportSalesforceClick) {
    const onClick = () => navigateToLink(contactsManagerAccountsUrl);
    return renderTask(isComplete, onClick);
  }

  return renderPopoverTask(isComplete, onCreateClick, onImportCSVClick, onImportSalesforceClick);
};

/*
 * Helpers.
 */

function renderPopoverTask(
  isComplete: boolean,
  onCreateClick: Handler,
  onImportCSVClick: Handler,
  onImportSalesforceClick: Handler,
) {
  return (
    <PopoverCoordinator
      renderButton={(hasHover) => renderTask(isComplete, noop, hasHover)}
      renderModal={(popoverProps) =>
        renderDropdown(popoverProps, onCreateClick, onImportCSVClick, onImportSalesforceClick)
      }
    />
  );
}

function renderTask(isComplete: boolean, onClick: Handler, hasHover?: boolean) {
  return (
    <SetupGuideTask
      label={<Trans id="accounts-setup-guide-task-add-account">Add an account</Trans>}
      isComplete={isComplete}
      hasHover={hasHover}
      onClick={onClick}
    />
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  onCreateClick: Handler,
  onImportCSVClick: Handler,
  onImportSalesforceClick: Handler,
) {
  // TODO: Add options for sync with Hubspot.
  return (
    <StyledSimpleDropdown {...popoverProps} width={225} placement="bottom-end">
      {renderDropdownItem(
        'editSmall',
        <Trans id="accounts-setup-guide-task-add-account-create">Create</Trans>,
        onCreateClick,
      )}
      {renderDropdownItem(
        'downloadLineSmall',
        <Trans id="accounts-setup-guide-task-add-account-import">Import CSV</Trans>,
        onImportCSVClick,
      )}
      {renderSalesforceDropdownItem(onImportSalesforceClick)}
    </StyledSimpleDropdown>
  );
}

function renderDropdownItem(iconName: IconName, content: ReactNode, onSelect: Handler) {
  return (
    <SimpleDropdownItem onSelect={onSelect}>
      <StyledDropdownDiv>
        <Icon name={iconName} />
        <span>{content}</span>
      </StyledDropdownDiv>
    </SimpleDropdownItem>
  );
}

function renderSalesforceDropdownItem(onSelect: Handler) {
  return (
    <SimpleDropdownItem onSelect={onSelect}>
      <StyledDropdownDiv>
        <img src={SalesforceImgSrc} alt="Salesforce logo" />
        <Trans id="accounts-setup-guide-task-from-salesforce">Sync with Salesforce</Trans>
      </StyledDropdownDiv>
    </SimpleDropdownItem>
  );
}
